import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VirtualKeyboard from './VirtualKeyboard';
import {
  getFingerInstruction,
  getHighlightKeysForLesson,
  HOME_INTRO_LINES,
  HOME_KEYS,
} from './fingerKeyMap';
import {
  calculateWpm,
  calculateAccuracy,
  formatDuration,
  isLessonComplete,
} from './typingTutorUtils';
import './fingeringTheme.css';

/**
 * Classic DOS-style fingering layout:
 * Header → Drill box (arrow cursor) → Instruction bar → Virtual keyboard → Footer
 */
export default function FingeringEngine({
  lesson,
  studentName = 'Student',
  targetWpm = 35,
  onComplete,
  onRetry,
  onCancel,
}) {
  const showIntro = lesson?.showHomeIntro === true;
  const [phase, setPhase] = useState(showIntro ? 'intro' : 'drill');
  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const target = lesson?.text || '';

  const currentChar = target[typed.length] || '';
  const instruction = useMemo(
    () => (currentChar || lesson?.instructionText
      ? getFingerInstruction(currentChar, lesson)
      : 'Place fingers on home row and start typing.'),
    [currentChar, lesson],
  );

  const highlightKeys = useMemo(
    () => getHighlightKeysForLesson(lesson, currentChar),
    [lesson, currentChar],
  );

  const reset = useCallback(() => {
    setTyped('');
    setStarted(false);
    setElapsed(0);
    setFinished(false);
    setResult(null);
    setPhase(showIntro ? 'intro' : 'drill');
    startTimeRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onRetry?.();
  }, [showIntro, onRetry]);

  useEffect(() => {
    reset();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [lesson?.id, reset]);

  const finishLesson = useCallback(
    (finalTyped) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      const timeSeconds = Math.max(
        1,
        startTimeRef.current
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : elapsed || 1,
      );
      let correct = 0;
      let errors = 0;
      for (let i = 0; i < finalTyped.length; i += 1) {
        if (finalTyped[i] === target[i]) correct += 1;
        else errors += 1;
      }
      const wpm = calculateWpm(correct, timeSeconds);
      const accuracy = calculateAccuracy(correct, finalTyped.length);
      const sessionResult = {
        wpm,
        accuracy,
        timeSpentSeconds: timeSeconds,
        errors,
        totalChars: finalTyped.length,
        correctChars: correct,
        goalMet: wpm >= (targetWpm || 35),
      };
      setResult(sessionResult);
      setFinished(true);
      onComplete?.(sessionResult);
    },
    [elapsed, onComplete, target, targetWpm],
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (finished) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Escape') {
        onCancel?.();
        return;
      }

      if (phase === 'intro') {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setPhase('drill');
        }
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        setTyped((prev) => prev.slice(0, -1));
        return;
      }

      if (e.key.length !== 1) return;
      e.preventDefault();

      if (!started) {
        setStarted(true);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 250);
      }

      setTyped((prev) => {
        if (prev.length >= target.length) return prev;
        const next = prev + e.key;
        if (isLessonComplete(next, target)) {
          setTimeout(() => finishLesson(next), 0);
        }
        return next;
      });
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [finished, phase, started, target, onCancel, finishLesson]);

  const liveWpm = started
    ? calculateWpm(
        typed.split('').filter((ch, i) => ch === target[i]).length,
        Math.max(1, elapsed),
      )
    : 0;

  const liveAccuracy = typed.length > 0
    ? calculateAccuracy(
        typed.split('').filter((ch, i) => ch === target[i]).length,
        typed.length,
      )
    : 100;

  const renderDrillChars = () =>
    target.split('').map((char, index) => {
      let className = 'fg-drill-char';
      if (index < typed.length) {
        className += typed[index] === char ? ' fg-done' : ' fg-wrong';
      } else if (index === typed.length) {
        className += ' fg-current';
      }
      const isCurrent = index === typed.length;
      return (
        <span key={index} className={className}>
          {isCurrent && <span className="fg-cursor-up">▲</span>}
          {char === ' ' ? '\u00A0' : char}
          {isCurrent && <span className="fg-cursor-down">▼</span>}
        </span>
      );
    });

  if (finished && result) {
    return (
      <div className="fg-screen">
        <div className="fg-top-bar">
          <span>Key Lesson</span>
          <span>{studentName}</span>
        </div>
        <div className="fg-result">
          <h3>Lesson Complete</h3>
          <div className="fg-result-stats">
            <div className="fg-result-stat">
              <strong>{result.wpm}</strong>
              WPM
            </div>
            <div className="fg-result-stat">
              <strong>{result.accuracy}%</strong>
              Accuracy
            </div>
            <div className="fg-result-stat">
              <strong>{formatDuration(result.timeSpentSeconds)}</strong>
              Time
            </div>
            <div className="fg-result-stat">
              <strong>{result.errors}</strong>
              Errors
            </div>
          </div>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={reset}
              sx={{ color: '#a5b4fc', borderColor: '#a5b4fc' }}
            >
              Try Again
            </Button>
            <Button variant="contained" onClick={onCancel} sx={{ bgcolor: '#6d28d9' }}>
              Back to Lessons
            </Button>
          </Box>
        </div>
        <div className="fg-footer">
          <span>
            <kbd>Esc</kbd> → Main Menu
          </span>
        </div>
      </div>
    );
  }

  if (phase === 'intro') {
    return (
      <div className="fg-screen">
        <div className="fg-top-bar">
          <span>Home Key Lesson</span>
          <span>{studentName}</span>
        </div>
        <div className="fg-body">
          <VirtualKeyboard homeOnly highlightKeys={[...HOME_KEYS]} />
          <div className="fg-intro-panel" style={{ marginTop: '1.25rem' }}>
            {HOME_INTRO_LINES.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => setPhase('drill')}
              sx={{
                bgcolor: '#0d9488',
                '&:hover': { bgcolor: '#0f766e' },
                fontWeight: 700,
              }}
            >
              Start Drill
            </Button>
          </Box>
        </div>
        <div className="fg-footer">
          <span>
            <kbd>Esc</kbd> → Main Menu
          </span>
          <span>
            <kbd>Space</kbd> to start
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="fg-screen">
      <div className="fg-top-bar">
        <span>Key Lesson — {lesson?.title || 'Fingering'}</span>
        <span>{studentName}</span>
      </div>

      <div className="fg-body">
        <div className="fg-drill-outer">
          <div className="fg-drill-inner">
            <div className="fg-drill-text">{renderDrillChars()}</div>
          </div>
        </div>

        <div className="fg-instruction-bar">{instruction}</div>

        <VirtualKeyboard
          activeKey={currentChar}
          highlightKeys={highlightKeys}
          homeOnly={false}
        />
      </div>

      <input
        ref={inputRef}
        className="tt-hidden-input"
        autoFocus
        aria-hidden="true"
        tabIndex={-1}
      />

      <div className="fg-footer">
        <span>
          <kbd>Esc</kbd> → Main Menu
        </span>
        <div className="fg-footer-stats">
          <span>{liveWpm} WPM</span>
          <span>{liveAccuracy}% Acc</span>
          <span>{formatDuration(elapsed)}</span>
        </div>
      </div>
    </div>
  );
}
